// @ts-nocheck
import LAWPAY_CONFIG from './lawpay-variables';
import Alert from '../../../lib/alert';
import * as payments from '../../../rest/lawpay';
import {
    deleteContact,
    disconnect,
    getAccounts,
    getContacts,
    signup,
    syncContacts,
    updateContact
} from '../../../rest/lawpay';
import store from '../../../store/store';
import * as FirmActions from '../../../store/actions/firmActions';
import util from '../../../lib/util';
import {isCasePayer} from '../../../Case/caseParties';
import PaymentBankAccount from '../types/PaymentBankAccount';
import PaymentCreditCard from '../types/PaymentCreditCard';
import paymentApi from '../paymentApi';
import lawpayLogo from './LawPay-Logo.png';
import {getCurrentFirm, getCurrentFirmId, getCurrentUserId, isPortalUser} from "../../../lib/picos/currentLogin";
import Contact from "../../../entities/contact";
import Lead from "../../../entities/lead";
import Matter from "../../../entities/matter";
import find from "../../../store/find";
import type BasePaymentMethod from "../types/BasePaymentMethod";
import type {PaymentAddress} from "../types/BasePaymentMethod";
import Address from "../../../entities/address";
import type {LawpayContact} from "../../../@types/lawpay";
import type {uuid} from "../../../@types/entity";

export type LawpayPaymentMeta = Partial<{
    amount: number;
    method: Partial<{
        number: string,
        accountType: any,
        cardType: string,
        type: string,
        fingerprint: string
    }>,
    authorization_code: string,
    id: string,
    type: string,
    chargeDate: Date,
    voidOrRefundDate: Date,
    status: string,
    amount_refunded: number,
    fingerprint: string
}>

export type PaymentTransaction = Partial<{
    accountName: string;
    accountNumber: string;
    isTrust: boolean;
    clientId: string;
    status: string;
    payerName: string;
    payerEmail: string;
    meta: LawpayPaymentMeta;
    contacts: Contact[]
}>

/**
 * Class to handle LawPay transactions
 */
class LawPay {

    /**
     * Are we currently in the process of connecting?
     *
     * @type {boolean}
     */
    static isConnecting: boolean = false;

    /**
     * Did we get the code?
     *
     * @type {boolean}
     */
    static gotCode: boolean = false;

    /**
     * The LawPay icon.
     *
     * @type {*}
     */
    static getIcon(): string {
        return lawpayLogo;
    }

    /**
     * The LawPay brand name.
     *
     * @returns {string}
     */
    static getName(): string {
        return 'LawPay';
    }

    /**
     * Start the OAuth connection flow for existing accounts.
     */
    static async connect() {
        // Redirect to the LawPay authentication uri.  Once authorized, the redirect_url above will be called,
        // which will be picked up in App.js, and call handleAuthentication (below)
        const redirect = LAWPAY_CONFIG.redirect_uri;

        const href = encodeURI(`https://secure.lawpay.com/oauth/authorize?` +
            `redirect_uri=${redirect}` +
            `&client_id=${LAWPAY_CONFIG.client_id}` +
            `&scope=payments` +
            `&response_type=code` +
            `&state=${getCurrentFirmId()}:${getCurrentUserId()}`);

        window.location.href = href;
    }

    /**
     * Start the OAuth connection flow for existing accounts.
     * @return {Promise<{applicationId: string, redirect_uri: string}>}
     */
    static async signup(): Promise<{ applicationId: string; redirect_uri: string; }> {
        try {
            return signup();
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, 0, 0, 'Error signing up for LawPay: ' + err.message));
        }
    }

    /**
     * Disconnect the LawPay OAuth account.
     *
     * @returns {Promise<void>}
     */
    static async disconnect(): Promise<void> {
        try {
            await disconnect();

            const firm = getCurrentFirm().clone();
            firm.paymentProcessor = null;
            store.dispatch(FirmActions.saveFirmQueue(firm));

            Alert.add(new Alert(Alert.SUCCESS, 0, 0,
                'LawPay accounts have been successfully disconnected from Outlaw Practice.  ' +
                'Please wait a moment while the server finishes processing.',
                5_000));
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, 0, 0, 'Error disconnecting from LawPay: ' + err.message));
        }
    }

    static async getContacts(): Promise<LawpayContact[]> {
        try {
            return await getContacts();
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, 0, 0, 'Error getting contacts from LawPay: ' + err.message));
        }
    }

    /**
     * Create a contact using the LawPay Contact api
     * @param {Contact[]} contacts
     * @returns {Promise<void>}
     */
    static async syncContacts(contacts: Contact[]): Promise<void> {
        try {
            return await syncContacts(contacts);
        }
        catch (err) {
            console.error(err);
        }
    }

    /**
     * Delete a contact using the processorContactId
     *
     * @param {string} processorContactId
     */
    static async deleteContact(processorContactId: string) {
        return deleteContact(processorContactId);
    }

    /**
     * Do we have a valid access token?
     *
     * @returns {boolean}
     */
    static isConnected(): boolean {
        return paymentApi.isConnected('lawpay');
    }

    /**
     * Get a list of accounts.
     */
    static async getAccounts() {
        return getAccounts();
    }

    /**
     * Update a contact in LawPay with new information
     * @param {Contact} contact
     * @returns {Promise<string>}
     */
    static async updateContact(contact: Contact): Promise<string> {
        return await updateContact(contact);
    }

    /**
     * Only relevant for Payrix.
     *
     * @param {uuid} txnId
     * @param {string} description
     */
    static async updateTxn(txnId: uuid, description: string): Promise<void> {
        throw new Error('Not implemented for LawPay');
    }

    /**
     * Is this contact eligible to have payments?
     *
     * Note that this seems to differ between LawPay and Payrix, so this should be at the processor level.
     *
     * @param {Contact} contact
     * @param {Lead | Matter} unsavedEntity
     * @returns {boolean} true if payments available
     */
    static paymentEligible(contact: Contact, unsavedEntity?: Lead | Matter): boolean {
        if (!contact) return false;
        if (!paymentApi.usesPayments()) return false;

        // A client or benefactor of a Case or Lead (OWA-1341: caseParties, never the lookup key)
        const bus = contact.isCompany;
        const basics = ((bus && contact.last) || (!bus && (contact.first && contact.last))) && contact.status !== 'D' && isCasePayer(contact.id, unsavedEntity);
        const hasEmails = !!store.getState().resources.find(r => r.entityId === contact.id && r.type === 'email' && r.isPrimary && r.canContact);
        const hasAddresses = !!store.getState().addresses.find(a => a.entityId === contact.id && a.isPrimary && a.canContact);

        return (basics && hasEmails && hasAddresses);
    };


    /**
     *
     * @param {Contact} contact
     * @returns {Promise<paymentMethod[]>}
     */
    static async getPaymentMethods(contact: Contact): Promise<(PaymentCreditCard | PaymentBankAccount)[]> {
        if (!contact.processorContactId) await payments.syncContacts([contact]);
        const result = await payments.getPaymentMethods(contact) as BasePaymentMethod[];

        const paymentMethods = result.map(pm => {
            pm.contact = contact;

            if (pm.isCard() && (pm as PaymentCreditCard).card_type) return new PaymentCreditCard(pm as any);
            else return new PaymentBankAccount(pm);
        });

        return paymentMethods || [];
    }

    /**
     * This method will create a new payment method and attach it to a contact.
     *
     * @param {Contact} contact
     * @param {BasePaymentMethod} paymentMethod
     * @returns {Promise<string>}
     */
    static async createPaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<string | any> {
        // Store the payment method
        return payments.createPaymentMethod(contact, LawPay.formatPaymentMethod(paymentMethod));
    }

    /**
     * Update the payment method.
     *
     * @param {Contact} contact
     * @param {BasePaymentMethod} paymentMethod
     * @returns {Promise<BasePaymentMethod>}
     */
    static async updatePaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<BasePaymentMethod> {
        // Store the payment method
        return payments.updatePaymentMethod(contact, LawPay.formatPaymentMethod(paymentMethod));
    }

    /**
     * Delete the payment method.
     *
     * @param {Contact} contact
     * @param {BasePaymentMethod} paymentMethod
     * @returns {Promise<string>}
     */
    static async deletePaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<string> {
        try {
            // Store the payment method
            return payments.deletePaymentMethod(contact, LawPay.formatPaymentMethod(paymentMethod));
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, contact.id, contact.addedOn, err.message));
            return null;
        }
    }

    /**
     * Format Outlaw address for LawPay.
     *
     * @param {Address} address
     * @returns {PaymentAddress}
     */
    static formatAddress(address: Address): PaymentAddress & {type: string} {
        return {
            type: address.label,
            line1: address.street,
            line2: address.unit,
            city: address.city,
            state_or_province: address.state,
            postal_code: address.zip,
            country_code: address.country
        }
    }

    /**
     * Format in LawPay's preferred format.
     *
     * @param {BasePaymentMethod} paymentMethod
     * @returns {any}
     */
    static formatPaymentMethod(paymentMethod: any): BasePaymentMethod {
        if (paymentMethod.paymentType === 'card') {
            const {
                id, paymentType, number, cardType, cvv, exp_month, exp_year, name, nickname, created, modified,
                fingerprint, status, email, address1, city, state, postal_code, phone, paymentToken
            } = paymentMethod;

            return new PaymentCreditCard({
                id,
                paymentType,
                number,
                cardType,
                cvv,
                exp_month,
                exp_year,
                name,
                nickname,
                created,
                modified,
                fingerprint,
                status,
                email,
                address: {address1, city, state, postal_code},
                phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                paymentToken
            });
        }
        else {
            const {
                id, paymentType, account_type, account_number, account_holder_type, routing_number, name, given_name,
                surname, nickname, email, address1, city, state, postal_code, phone, paymentToken
            } = paymentMethod as PaymentBankAccount;

            if (account_holder_type === 'individual')
                return new PaymentBankAccount({
                    id,
                    paymentType,
                    account_number,
                    account_type: account_type.toUpperCase(),
                    account_holder_type: account_holder_type.toLowerCase(),
                    routing_number,
                    given_name,
                    surname,
                    nickname,
                    email,
                    address: {address1, city, state, postal_code},
                    phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                    paymentToken
                });
            else
                return new PaymentBankAccount({
                    id,
                    paymentType,
                    account_number,
                    account_type: account_type.toUpperCase(),
                    account_holder_type: account_holder_type.toLowerCase(),
                    routing_number,
                    name,
                    given_name,
                    surname,
                    nickname,
                    email,
                    address: {
                        address1,
                        city,
                        state,
                        postal_code
                    },
                    phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                    paymentToken
                })
        }
    }

    /**
     * Create a payment using the given payment method for the given amount.
     *
     * @param {string} paymentMethodId - payment method ID from the provider
     * @param {number} amount - amount of the transaction.  Debits will be positive numbers, credits will be negative!
     * @param {string} accountId - the account in which to create the charge
     * @param {string} [transaction] - the related transaction (only used with Payrix)
     * @returns {Promise<BasePaymentMethod>}
     */
    static async createPayment(paymentMethodId: string, amount: number, accountId: string, transaction: string): Promise<Result<BasePaymentMethod, Error>> {
        return payments.createPayment(paymentMethodId, amount, accountId);
    }

    /**
     * Fully refund a transaction.
     *
     * @param {string} transactionId
     * @param {number} amount - amount in $.cc
     * @returns {Promise<any>}
     */
    static async refundTransaction(transactionId: string, amount: number): Promise<Transaction> {
        return payments.refundTransaction(transactionId, amount);
    }

    /**
     * Void a transaction.
     *
     * @param {string} transactionId
     * @returns {Promise<*>}
     */
    static async voidTransaction(transactionId: string): Promise<Transaction> {
        return payments.voidTransaction(transactionId);
    }

    /**
     * Get details on a transaction.
     *
     * @param {string} transactionId
     * @returns {Promise<PayrixTransaction>}
     */
    static async getTransaction(transactionId: string): Promise<Transaction> {
        return payments.getTransaction(transactionId);
    }

    /**
     * Get all transactions for a given date range.
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @returns {Promise<*>}
     */
    static async getTransactions(startDate, endDate) {
        const resultsPromise = payments.getTransactions(startDate, endDate);

        // Cache lawpay accounts
        const accountsPromise = getAccounts();

        // Wait for these calls to return
        await Promise.allSettled([resultsPromise, accountsPromise]);

        // Resolve results and accounts
        const results = await resultsPromise;
        const accounts = await accountsPromise;

        // We will process the results to a standardized format
        const processedResults: PaymentTransaction[] = [];

        for (let i = 0; i < results.length; i++) {
            const tr = results[i];
            const o: PaymentTransaction = {};
            const account = accounts.find(a => a.theirId === tr.account_id);

            o.accountName = account.name;
            o.accountNumber = account.theirId;
            o.isTrust = account.trustAccount;
            o.clientId = tr.client_id;
            o.status = tr.status;
            o.payerName = tr.method.name;
            o.payerEmail = tr.method.email;
            o.meta = paymentApi.getDelegate().parsePaymentMeta(tr);

            // Find the related contact
            if (tr.method.email) {
                o.contacts = store.getState().resources
                    .filter(r => r.type === 'email' && r.value === tr.method.email)
                    .map(r => find<Contact>('contacts', r.entityId));
            }
            else o.contacts = [];

            processedResults.push(o);
        }

        return processedResults;
    }

    /**
     * Parse out fields we care about from a payment/void/refund, specifically:
     * - authorization_code: for display to the user
     * - id: the ID of the transaction, for later lookup
     * - status: e.g. VOIDED, AUTHORIZED, PENDING, COMPLETED, ACTIVE
     * - type: e.g. CHARGE, REFUND, CREDIT (note that void returns CHARGE)
     * - amount: the amount of the charge
     * - amount_refunded: the amount refunded (if a refund)
     * - chargeDate: the date the payment method was initially charged
     * - voidOrRefundDate: the date the payment was (last) voided or refunded
     *
     * @param {any} meta
     * @returns {LawpayPaymentMeta}
     */
    static parsePaymentMeta(meta: any): LawpayPaymentMeta {
        const {id, type, status, authorization_code, amount, amount_refunded, created, modified, method} = meta;
        return {
            id,
            type,
            status,
            authorization_code,
            amount: amount / 100,
            amount_refunded: amount_refunded / 100,
            chargeDate: new Date(created),
            voidOrRefundDate: new Date(modified),
            method: {
                type: method.type,
                number: (method.number || method.account_number)?.replaceAll('*', ''),
                cardType: method.type === 'card' ? util.snakeToText(method.card_type) : null,
                accountType: method.type === 'bank' ? method.account_type.toLowerCase() : null,
                fingerprint: method.fingerprint
            }
        };
    }

    /**
     * Get the current status of a transaction.
     *
     * This will return an array of status flags.  Specifically:
     *
     * - NONE: no processor action is associated with this transaction.
     * - INCOMPLETE:  the transaction is in progress, and can be voided.
     * - COMPLETED: the transaction has been settled, and can be refunded but not voided.
     * - VOID: the transaction has been voided.  This is immediate, so once voided nothing can be done.
     * - REFUND: the transaction is being/has been refunded.  Check for INCOMPLETE vs COMPLETED.
     * - CHARGE: the transaction is being/has been processed as a charge.  Check for INCOMPLETE vs COMPLETED.
     *
     * Valid combinations are:
     * - NONE
     * - CHARGE/INCOMPLETE
     * - CHARGE/COMPLETE
     * - VOID/COMPLETE
     * - REFUND/INCOMPLETE
     * - REFUND/COMPLETE
     *
     * @param {object} tr
     * @returns {flags: Array.<'CHARGE'|'REFUND'|'INCOMPLETE'|'COMPLETE'|'VOID'>, message: string} - an array of flags indicating current status.
     */
    static getCurrentStatus(tr: Transaction) {
        const meta = tr.processorPaymentMeta;
        if (!meta) return {flags: [], message: 'No status available.'}
        
        const {status, type, amount} = meta;

        const amt = util.formatCurrency(Number.parseFloat(amount), true);

        if (type === 'CHARGE') {
            if (status === 'AUTHORIZED') return {
                flags: ['CHARGE', 'INCOMPLETE'],
                message: `Payment of ${amt} has been submitted${isPortalUser() ? '' : ', waiting for funds to settle'}.`
            };
            else if (status === 'COMPLETED') return {
                flags: ['CHARGE', 'COMPLETE'],
                message: `Payment of ${amt} has settled.`
            };
            else if (status === 'VOIDED') return {
                flags: ['CHARGE', 'VOID'],
                message: `Payment of ${amt} has been voided.`
            };
            else throw Error(`Unknown status received: '${status}'`);
        }
        else if (type === 'REFUND') {
            if (status === 'AUTHORIZED') return {
                flags: ['REFUND', 'INCOMPLETE'],
                message: `Refund of ${amt} has been submitted${isPortalUser() ? '' : ', waiting for funds to settle'}.`
            };
            else if (status === 'COMPLETED' || status === 'REFUND') return {
                flags: ['REFUND', 'COMPLETE'],
                message: `Refund of ${amt} has been completed.`
            };
            else if (status === 'VOIDED') return {
                flags: ['REFUND', 'VOID'],
                message: `Refund of ${amt} has been voided.`
            };
            else throw Error(`Unknown type received: '${type}'`);
        }
    }

    /**
     * Get the completion date from the metadata.
     *
     * @param {object} meta
     * @return {Date|null}
     */
    static getCompletionDate(meta) {
        return meta?.completion_date;
    }
}

export default LawPay;
